import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'antd'
import PlusIcon from 'mdi-react/PlusIcon'
import { RootStateOrAny, useSelector } from 'react-redux'

import { WhiteGreyBtn } from 'Stories/Button/Button.stories'
import { colors } from 'Styled/variables'
import arrowLeftIcon from 'Shared/icons/arrow-left.svg'
import closeIcon from 'Shared/icons/close-cross.svg'
import { useDebounce } from 'use-debounce/lib'

import { getAllPlaylistsSpotifyIds } from 'Redux/selectors/playLists'
import { FindPlaylistMobileProps } from './FindPlaylistMobileProps'

import * as S from './styles'
import AlertErrorMessageWithIcon from '../../../../Shared/components/AlertErrorMessageWithIcon'
import { findPlaylist, onAddPlaylist } from '../helper'

const FindPlaylistMobile: FunctionComponent<FindPlaylistMobileProps> = ({
  onCheckPlaylist,
  setRecentlyAddedPlaylist,
  isLoading,
  setIsLoading,
}) => {
  const [modal, setModalValue] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState<string>()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [newPlaylistToSet, setNewPlaylistToSet] = useState<any>()
  const [error, setError] = useState<string>('')

  const InputRef = useRef<HTMLInputElement>()
  const toggle = () => {
    setModalValue(!modal)
  }
  const { t } = useTranslation()
  const accessToken: string = useSelector(
    (state: RootStateOrAny) => state.spotifyToken.accessToken
  )

  const spotifyId = useSelector(
    (state: RootStateOrAny) => state.auth.user.spotifyId
  )

  const allPlaylistsSpotifyIds = useSelector(getAllPlaylistsSpotifyIds)

  const onItemClickHandler = () =>
    onAddPlaylist({
      onCheckPlaylist,
      setRecentlyAddedPlaylist,
      toggle,
      // @ts-ignore
      searchValue,
    })

  const findPlaylistHandler = () =>
    findPlaylist({
      accessToken,
      // @ts-ignore
      searchValue,
      setNewPlaylistToSet,
      setIsLoading,
      spotifyId,
      setError,
      allPlaylistsSpotifyIds,
    })

  const debouncedSearchValue = useDebounce(searchValue, 500)

  useEffect(() => {
    if (
      debouncedSearchValue[0] &&
      !debouncedSearchValue[0].match(/playlist/g)
    ) {
      setError('validation.incorrectLink')
      return
    }
    if (debouncedSearchValue[0]) {
      findPlaylistHandler()
    } else {
      setError('')
    }
  }, [debouncedSearchValue[0]])

  useEffect(() => {
    isLoading && setError('')
  }, [isLoading])

  const renderPlaylist = useMemo(() => {
    if (newPlaylistToSet) {
      return (
        <S.Playlist onClick={onItemClickHandler}>
          <S.PlaylistAvatar
            src={
              newPlaylistToSet.images.length
                ? newPlaylistToSet?.images[0].url
                : null
            }
            alt=""
          />
          <S.PlaylistInfo>
            <S.PlaylistTitle>{newPlaylistToSet.name}</S.PlaylistTitle>
            <S.PlaylistAuthor>{`${t('playlistsPage.by')} ${
              newPlaylistToSet.owner.display_name
            }`}</S.PlaylistAuthor>
          </S.PlaylistInfo>
        </S.Playlist>
      )
    }
    return null
  }, [newPlaylistToSet])

  const clearInput = useCallback(() => {
    if (InputRef && InputRef.current?.value) {
      InputRef.current.value = ''
      setSearchValue('')
    }
  }, [InputRef])

  const meetConditionsLink =
    'http://help.soundcamps.com/en/articles/5541325-requirements-for-your-playlists'

  const renderModal = () => (
    <Modal
      visible={modal}
      footer={null}
      // @ts-ignore
      closable={null}
    >
      <S.Container active={modal}>
        <S.Content active={modal}>
          <S.SearchContainer>
            <S.BackButton src={arrowLeftIcon} alt="" onClick={toggle} />
            <S.SearchInput
              // @ts-ignore
              ref={InputRef}
              placeholder={t('playlistsPage.mobileAddPlaylistInput')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearchValue(e.target.value)
              }}
            />

            <S.ClearButton src={closeIcon} alt="" onClick={clearInput} />
          </S.SearchContainer>
          <S.AlertWrapper>
            <AlertErrorMessageWithIcon
              type="mobile"
              isShow={!isLoading && !!error}
              errorText={t(error)}
            />
            {error === 'validation.notRequirementsPlaylist' && (
              <S.MeetConditionsBtn href={meetConditionsLink} target="_blank">
                {t('validation.minimalPlatformRequirements')}
              </S.MeetConditionsBtn>
            )}
          </S.AlertWrapper>
          {isLoading && (
            <S.SearchingWrapper>
              {t('playlistsPage.searching')}
            </S.SearchingWrapper>
          )}
          {renderPlaylist}
        </S.Content>
      </S.Container>
    </Modal>
  )

  return (
    <>
      <WhiteGreyBtn
        color="white"
        // @ts-ignore
        disabled={isLoading}
        onClick={toggle}
      >
        <S.IconContainer>
          <PlusIcon size="16" color={colors.green} />
        </S.IconContainer>
        {t('playlistsPage.addManually')}
      </WhiteGreyBtn>
      {renderModal()}
    </>
  )
}

export default FindPlaylistMobile
